import type { ReportSkillResult } from "@/generated/graphql";
import { type Dictionary, useT } from "@/i18n";
import { useEffect, useRef, useState } from "react";
import { HiExclamation, HiLockClosed } from "react-icons/hi";
import {
  PROVISIONAL_SKILL,
  REPORT_SKILL_IDS,
  TREND_STYLES,
  UNAVAILABLE_SKILL,
  skillLabel,
} from "./reportTokens";

/** A 0% axis collapses the polygon onto the centre and reads as a broken chart, so every vertex is
 * floored — only the geometry, never the percentage in the label. */
const MIN_PLOT_PERCENT = 20;
const RINGS = [0.25, 0.5, 0.75, 1];
const GRID_COLOR = "#DFDFDF";
const PLOT_COLOR = "#AAAAFF";
const LABEL_WIDTH = 80;
const LABEL_HEIGHT = 40;
const LABEL_GAP = 10;
const MIN_RADIUS = 56;
const MAX_RADIUS = 104;

/** A student with no practice still gets a full chart — every axis locked at the floor — rather
 * than an empty card, so the shape of the report is the same before and after their first play. */
const LOCKED_SKILLS: ReportSkillResult[] = REPORT_SKILL_IDS.map((skill) => ({
  skill,
  score: null,
  distinctSlideCount: 0,
  qualifies: false,
  trend: null,
}));

interface AxisPoint {
  x: number;
  y: number;
  cos: number;
  sin: number;
}

function axisPoint(
  cx: number,
  cy: number,
  radius: number,
  index: number,
  count: number
): AxisPoint {
  const angle = ((-90 + (360 / count) * index) * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return { x: cx + radius * cos, y: cy + radius * sin, cos, sin };
}

/** The card is a grid cell of unknown width, and the labels ring the plot — so the plot's radius
 * is whatever is left after reserving a label on each side. */
function useContainerWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, width] as const;
}

function toPolygonPoints(points: AxisPoint[]): string {
  return points.map((point) => `${point.x},${point.y}`).join(" ");
}

function getScore(result: ReportSkillResult): number | null {
  return result.score == null ? null : Math.round(result.score);
}

/** "provisional" is a real score the student hasn't yet backed with MIN_DISTINCT_SLIDES_TO_QUALIFY
 * slides (see reportSkills.service.ts) — shown, but flagged. "locked" means no scored play at all. */
function getSkillState(result: ReportSkillResult): "locked" | "provisional" | "scored" {
  if (result.score == null) return "locked";
  return result.qualifies ? "scored" : "provisional";
}

function AxisLabel({
  result,
  point,
  t,
}: { result: ReportSkillResult; point: AxisPoint; t: Dictionary }) {
  const score = getScore(result);
  const state = getSkillState(result);
  const trend = result.trend ? TREND_STYLES[result.trend] : null;
  const TrendIcon = trend?.icon;

  // Anchored away from the chart: side axes hang off their vertex, top/bottom axes centre on it.
  const isRight = point.cos > 0.3;
  const isLeft = point.cos < -0.3;
  const left = isRight
    ? point.x + 6
    : isLeft
      ? point.x - 6 - LABEL_WIDTH
      : point.x - LABEL_WIDTH / 2;
  const top =
    point.sin < -0.3
      ? point.y - LABEL_HEIGHT
      : point.sin > 0.3
        ? point.y
        : point.y - LABEL_HEIGHT / 2;

  return (
    <div
      className={`absolute flex flex-col justify-center gap-0.5 ${
        isRight ? "items-start" : isLeft ? "items-end" : "items-center"
      }`}
      style={{ left, top, width: LABEL_WIDTH, height: LABEL_HEIGHT }}
    >
      <span className="w-full truncate font-kumbh text-[13px] font-bold text-app-ink">
        {skillLabel(t, result.skill)}
      </span>
      {state === "locked" ? (
        // No scored play at all — a lock rather than "0%", which would read as a measured failure.
        <span
          className="flex h-4 w-4 items-center justify-center rounded-full"
          style={{ backgroundColor: UNAVAILABLE_SKILL.background }}
          title={t["report.skills.locked"]}
        >
          <HiLockClosed className="h-2.5 w-2.5" style={{ color: UNAVAILABLE_SKILL.color }} />
        </span>
      ) : (
        <span className="flex items-center gap-1">
          <span className="font-kumbh text-[13px] font-bold tabular-nums text-app-ink">
            {score}%
          </span>
          {state === "provisional" ? (
            <span
              className="flex h-4 w-4 items-center justify-center rounded-full"
              style={{ backgroundColor: PROVISIONAL_SKILL.background }}
              title={t["report.skills.provisional"]}
            >
              <HiExclamation className="h-3 w-3" style={{ color: PROVISIONAL_SKILL.color }} />
            </span>
          ) : (
            trend &&
            TrendIcon && (
              <span
                className="flex h-4 w-4 items-center justify-center rounded-full"
                style={{ backgroundColor: trend.background }}
              >
                <TrendIcon className="h-2.5 w-2.5" style={{ color: trend.color }} />
              </span>
            )
          )}
        </span>
      )}
    </div>
  );
}

/** Every skill on one radar, locked ones included, so a gap reads as "not measured" not "not shown". */
export function SkillsBreakdownCard({ skills }: { skills: ReportSkillResult[] }) {
  const t = useT();
  const [containerRef, width] = useContainerWidth();

  const radius = Math.min(Math.max(width / 2 - LABEL_WIDTH - LABEL_GAP, MIN_RADIUS), MAX_RADIUS);
  const chartHeight = 2 * (radius + LABEL_GAP + LABEL_HEIGHT);
  const cx = width / 2;
  const cy = chartHeight / 2;

  const plottedSkills = skills.length > 0 ? skills : LOCKED_SKILLS;
  const axes = plottedSkills.map((result, index) => {
    const score = getScore(result);
    return {
      result,
      score,
      spoke: axisPoint(cx, cy, radius, index, plottedSkills.length),
      plot: axisPoint(
        cx,
        cy,
        (radius * Math.max(score ?? 0, MIN_PLOT_PERCENT)) / 100,
        index,
        plottedSkills.length
      ),
      label: axisPoint(cx, cy, radius + LABEL_GAP, index, plottedSkills.length),
    };
  });
  // Every skill locked (the brand-new-student state) plots as a flat floor polygon, so it takes the
  // muted "unavailable" accent rather than reading as a real measured score.
  const accent = axes.some((axis) => axis.score != null) ? PLOT_COLOR : UNAVAILABLE_SKILL.color;
  const hasProvisionalSkill = axes.some((axis) => getSkillState(axis.result) === "provisional");

  return (
    <div className="app-card px-5 py-4">
      <p className="app-label">{t["report.skills.title"]}</p>

      <div ref={containerRef} className="relative mt-3" style={{ height: chartHeight }}>
        {width > 0 && (
          <>
            <svg width={width} height={chartHeight} aria-hidden="true">
              {RINGS.map((ring) => (
                <polygon
                  key={ring}
                  points={toPolygonPoints(
                    axes.map((_, index) => axisPoint(cx, cy, radius * ring, index, axes.length))
                  )}
                  fill="none"
                  stroke={GRID_COLOR}
                  strokeWidth={1}
                />
              ))}
              {axes.map((axis) => (
                <line
                  key={axis.result.skill}
                  x1={cx}
                  y1={cy}
                  x2={axis.spoke.x}
                  y2={axis.spoke.y}
                  stroke={GRID_COLOR}
                  strokeWidth={1}
                />
              ))}
              <polygon
                points={toPolygonPoints(axes.map((axis) => axis.plot))}
                fill={accent}
                fillOpacity={0.3}
                stroke={accent}
                strokeWidth={2}
                strokeLinejoin="round"
              />
              {axes.map((axis) => (
                <circle
                  key={axis.result.skill}
                  cx={axis.plot.x}
                  cy={axis.plot.y}
                  r={3.5}
                  fill={axis.score != null ? accent : UNAVAILABLE_SKILL.color}
                />
              ))}
            </svg>

            {axes.map((axis) => (
              <AxisLabel key={axis.result.skill} result={axis.result} point={axis.label} t={t} />
            ))}
          </>
        )}
      </div>

      {hasProvisionalSkill && (
        <p className="caption mt-2 flex items-center gap-1.5 text-app-muted">
          <HiExclamation
            className="h-3.5 w-3.5 shrink-0"
            style={{ color: PROVISIONAL_SKILL.color }}
          />
          {t["report.skills.footnote"]}
        </p>
      )}
    </div>
  );
}
